import { Component, EventEmitter, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { SupportTicket } from 'src/app/models/support-ticket';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { SupportTicketService } from 'src/app/services/support-ticket.service';
import { UiBlockService } from 'src/app/services/ui-block.service';

@Component({
  selector: 'app-user-support-ticket',
  templateUrl: './user-support-ticket.component.html',
  styleUrls: ['../../app.component.scss', './user-support-ticket.component.scss'],
  standalone: false
})
export class UserSupportTicketComponent {

  modalTitle = 'Open New Ticket';

  headerTitle = '';
  description = '';

  attachments: File[] = [];

  creating = false;

  ticketType: 'DEFECT' | 'IMPROVEMENT' = 'DEFECT';

  readonly maxFiles = 5;
  readonly maxTotalSizeMb = 20;
  readonly allowedContentTypes = new Set([
    'image/png',
    'image/jpeg',
    'application/pdf',
    'text/plain',
    'application/zip',
    'application/x-zip-compressed'
  ]);

  @Output() created = new EventEmitter<SupportTicket>();

  constructor(
    public bsModalRef: BsModalRef,
    private supportTicketService: SupportTicketService,
    private modalControlService: ModalControlService,
    private uiBlockService: UiBlockService
  ) { }

  isFormValid(): boolean {
    return !!this.headerTitle.trim() && !!this.description.trim();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = input?.files;

    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const newAttachments = [...this.attachments];

    for (let index = 0; index < selectedFiles.length; index++) {
      const file = selectedFiles.item(index);
      if (!file) {
        continue;
      }

      const alreadyAdded = newAttachments.some((attachment) => {
        return attachment.name === file.name
          && attachment.size === file.size
          && (attachment.type || '') === (file.type || '');
      });

      if (alreadyAdded) {
        continue;
      }

      newAttachments.push(file);
    }

    if (!this.validateFileTypes(newAttachments) || !this.validateAttachments(newAttachments)) {
      input.value = '';
      return;
    }

    this.attachments = newAttachments;
    input.value = '';
  }

  removeAttachment(index: number): void {
    if (index < 0 || index >= this.attachments.length) {
      return;
    }

    this.attachments.splice(index, 1);
  }

  submitTicket(): void {
    if (!this.isFormValid()) {
      this.modalControlService.showMesssageModal(this.modalTitle, 'Title and Description are mandatory.');
      return;
    }

    if (!this.validateFileTypes(this.attachments) || !this.validateAttachments(this.attachments)) {
      return;
    }

    this.creating = true;
    this.uiBlockService.startBlock('Submitting support ticket...');

    const title = this.headerTitle.trim();
    const description = this.description.trim();

    this.supportTicketService.createTicket({
      title,
      description,
      ticketType:this.ticketType,
      exportToJira: false
    }).pipe(
      switchMap((ticket) => {
        if (typeof ticket.id !== 'number' || this.attachments.length === 0) {
          return of({ ticket, attachmentsFailed: false });
        }

        const ticketId = ticket.id;

        return this.supportTicketService.addAttachments(ticketId, this.attachments, true).pipe(
          map(() => ({ ticket, attachmentsFailed: false })),
          catchError((error) => {
            console.error('Error adding one or more attachments to support ticket', error);
            return of({ ticket, attachmentsFailed: true });
          })
        );
      }),
      finalize(() => {
        this.creating = false;
        this.uiBlockService.stopBlock();
      })
    ).subscribe({
      next: ({ ticket, attachmentsFailed }) => {
        if (attachmentsFailed) {
          this.modalControlService.showMesssageModal(this.modalTitle, 'Ticket submitted, but one or more attachments could not be saved.');
          this.created.emit(ticket);
          this.bsModalRef.hide();
          return;
        }

        this.modalControlService.showMesssageModal(this.modalTitle, 'Ticket submitted successfully.');
        this.created.emit(ticket);
        this.bsModalRef.hide();
      },
      error: (error) => {
        console.error('Error submitting support ticket', error);
        this.modalControlService.showMesssageModal(this.modalTitle, 'There was an error submitting your ticket.');
      }
    });
  }

  formatFileSize(fileSize?: number | null): string {
    if (fileSize == null || fileSize < 0) {
      return '';
    }

    if (fileSize < 1024) {
      return `${fileSize} B`;
    }

    const kilobytes = fileSize / 1024;
    if (kilobytes < 1024) {
      return `${kilobytes.toFixed(1)} KB`;
    }

    return `${(kilobytes / 1024).toFixed(1)} MB`;
  }

  get totalAttachmentSize(): number {
    return this.attachments.reduce((sum, file) => sum + file.size, 0);
  }

  private validateAttachments(attachments: File[]): boolean {
    if (attachments.length > this.maxFiles) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        `Maximum ${this.maxFiles} files allowed.`
      );
      return false;
    }

    const totalSize = attachments.reduce((sum, file) => sum + file.size, 0);
    const maxBytes = this.maxTotalSizeMb * 1024 * 1024;

    if (totalSize > maxBytes) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        `Maximum total attachment size is ${this.maxTotalSizeMb} MB.`
      );
      return false;
    }

    return true;
  }

  private validateFileTypes(files: File[]): boolean {
    const invalidFile = files.find(file =>
      file.size === 0 || !this.allowedContentTypes.has(file.type)
    );

    if (!invalidFile) {
      return true;
    }

    const message = invalidFile.size === 0
      ? `The file "${invalidFile.name}" is empty.`
      : `The file "${invalidFile.name}" has an unsupported type.`;

    this.modalControlService.showMesssageModal(this.modalTitle, message);
    return false;
  }

}
